<template>
	<div :class="generateClassName">
		<vue-numeric
			v-model="vm.saturn"
			:label
			:invalid
			:precision="'precision' in $props ? precision : 2"
			:minus="allowNegative"
			:placeholder
			:readonly
			:disabled
			:max="maxValue"
			:min="minValue"
			:maxlength
			:minlength
			:thousandSeparator
			:showIcon
			:icon
			:copy
			:comparer
			:get
			:set
			:cssClass
			:height
			:width
			:infoIconObj
			:separator
			:iconLeft="!iconRight"
			@blur="onBlurHandler"
			@focus="onFocusInput"
			@input="onInputHandler"
			@change="onChangeHandler"
		>
			<template
				#validation
				v-if="util.hasKey($slots, 'validation')"
			>
				<slot name="validation"></slot>
			</template>
			<template
				#action
				v-if="util.hasKey($slots, 'action')"
			>
				<slot name="action"></slot>
			</template>
		</vue-numeric>
	</div>
</template>
<script lang="ts">
	export interface IInputNumberProps {
		saturn?: any;
		cdk?: any;
		label?: string;
		placeholder?: string;
		invalid?: boolean;
		readonly?: boolean;
		disabled?: any;
		precision?: any;
		negative?: any;
		isPercent?: boolean;
		copy?: any;
		comparer?: any;
		get?: any;
		set?: any;
		showIcon?: boolean;
		increment?: string;
		max?: string | number;
		min?: string | number;
		thousandSeparator?: any;
		maxlength?: number;
		minlength?: number;
		infoIconObj?: any;
		cssClass?: string;
		width?: any;
		height?: any;
		separator?: any;
		iconSide?: 'left' | 'right';
		customIcon?: any;
	}
</script>
<script setup lang="ts">
	import { computed, onMounted, reactive, ref, useSlots, watch } from 'vue';
	import util from '@core/services/util';
	import vstore from '@core/services/vstore';
	import VueNumeric from '@core/components/private/vue-numeric.vue';

	//#region Common Variables
	const {
		saturn,
		cdk,
		label,
		placeholder,
		invalid,
		readonly,
		disabled,
		precision,
		negative,
		isPercent,
		copy,
		comparer,
		get,
		set,
		showIcon = true,
		increment,
		max,
		min,
		thousandSeparator,
		maxlength,
		minlength,
		infoIconObj,
		cssClass,
		width,
		height,
		separator,
		iconSide = 'left',
		customIcon,
	} = defineProps<IInputNumberProps>();
	const emits = defineEmits(['change', 'input', 'blur', 'focus', 'update:saturn']);
	const $slots = useSlots();
	//#endregion

	const vm = reactive({
		saturn: null as number,
		cdk: null,
	});

	const errorHover = ref(false);
	const wantIcon = ref(null);
	const incrementValue = ref<number>(null);
	const maxValue = ref<number>(null);
	const minValue = ref<number>(null);
	const icon = ref<string>(isPercent ? 'fas fa-percent' : customIcon ? customIcon : 'fas fa-hashtag');
	const iconRight = ref<boolean>(false);
	const isDirty = ref<boolean>(false);

	const cdkValueExists = computed(() => cdk !== undefined);
	const allowNegative = computed(() => (negative ? negative : true));
	const generateClassName = computed(() => {
		let className = 'InputNumber';
		if (!wantIcon.value) className += ' not-icon';

		return className;
	});

	if (iconSide.toLowerCase() === 'right') {
		iconRight.value = true;
	}

	onMounted(() => {
		init();
		wantIcon.value = showIcon ? showIcon : true;
		incrementValue.value = increment ? parseInt(increment) : 0;

		if (max) {
			maxValue.value = typeof max == 'string' ? parseInt(max) : max;
		} else {
			maxValue.value = 9007199254740991;
		}

		if (min) {
			minValue.value = typeof min == 'string' ? parseInt(min) : min;
		} else {
			minValue.value = -9007199254740991;
		}

		watch(
			() => saturn,
			function (newValue) {
				init();
			},
			{ immediate: true },
		);
	});

	function init() {
		vm.saturn = set ? set(saturn) : saturn != null && typeof saturn != 'undefined' ? saturn : null;
	}

	function incrementToValue(operation: any, value: any) {
		let newValue = null;
		if (operation == 'up') {
			newValue = vm.saturn + value;
			if (newValue > maxValue.value) {
				vm.saturn = maxValue.value;
			} else {
				vm.saturn = newValue;
			}
		} else if (operation == 'down') {
			newValue = vm.saturn - value;
			if (newValue < minValue.value) {
				vm.saturn = minValue.value;
			} else {
				vm.saturn = newValue;
			}
		}
	}

	// #region Event Handlers
	function onBlurHandler($e: any) {
		if (!$e.target.value) {
			vm.saturn = null;
		}

		if (isDirty.value) {
			emits('blur', $e);
		}

		isDirty.value = false;
	}

	function onFocusInput($e: any) {
		emits('focus');
	}

	function onInputHandler(val: any) {
		isDirty.value = true;
		if (!get) {
			if (val !== null && typeof val !== 'string' && typeof val !== 'number') {
				emits('input', val);
			} else {
				emits('update:saturn', val);
			}
		}
		emits('input', val);
	}

	function onChangeHandler(val: any) {
		emits('change', val);
	}
	//#endregion
</script>
<style>
	.InputNumber .input-component.not-icon .saturn-textbox fieldset.floatingLabel {
		padding: 0 10% 0 5px;
	}

	.InputNumber .input-component.not-icon label.label-root {
		left: 5px;
		max-width: 98%;
	}
</style>
